import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Trash2,
  Plus,
  Eye,
  Pencil,
} from "lucide-react";
import { AdminGuard } from "../../components/AdminGuard";
import { ConfirmAction } from "../../components/ConfirmAction";
import { ArticleBody } from "../../components/ArticleBody";
import { Wordmark } from "../../components/Header";
import { usePageMeta } from "../../components/Common";
import { ARTICLE_CATEGORIES } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  draft: "Draft",
  review: "Ready for Review",
  published: "Published",
  archived: "Archived",
};

const EMPTY = {
  title: "",
  slug: "",
  category: ARTICLE_CATEGORIES[0],
  read_time: "5 min read",
  excerpt: "",
  seo_title: "",
  seo_description: "",
  og_title: "",
  og_description: "",
  status: "draft",
  body: [],
};

const BLOCK_LABELS = {
  p: "Paragraph",
  h2: "Subheading",
  questions: "Reflection questions",
  try: "Try This activity",
};

const slugify = (t) =>
  t
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const inputClass =
  "w-full rounded-sm border border-navy/20 bg-white px-4 py-2.5 text-sm text-charcoal outline-none transition-colors focus:border-crimson focus:ring-2 focus:ring-crimson/15";
const labelClass = "mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-navy/70";

const Field = ({ label, hint, children, testid }) => (
  <div data-testid={testid}>
    <label className={labelClass}>{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-charcoal/50">{hint}</p>}
  </div>
);

const Editor = () => {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loaded, setLoaded] = useState(isNew);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  usePageMeta("Article Editor — English for Leaders", undefined, "/admin", {
    noindex: true,
  });

  useEffect(() => {
    if (isNew) return;
    let live = true;
    axios
      .get(`${API}/admin/articles/${id}`, { withCredentials: true })
      .then((r) => {
        if (!live) return;
        const { id: _id, order, source, created_at, updated_at, published_at, ...rest } = r.data;
        setForm({ ...EMPTY, ...rest });
        setSlugTouched(true);
        setLoaded(true);
      })
      .catch((e) => {
        if (e?.response?.status === 401) navigate("/admin/login", { replace: true });
        else {
          toast.error("Could not load the article.");
          navigate("/admin");
        }
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onTitle = (e) => {
    const title = e.target.value;
    setForm((f) => ({
      ...f,
      title,
      slug: slugTouched ? f.slug : slugify(title),
    }));
  };

  const isPublished = form.status === "published";
  const canSave = form.title.trim() && form.slug.trim() && form.category.trim();

  const save = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim(),
      category: form.category,
      read_time: form.read_time,
      excerpt: form.excerpt,
      body: form.body,
      seo_title: form.seo_title,
      seo_description: form.seo_description,
      og_title: form.og_title,
      og_description: form.og_description,
      status: form.status === "review" ? "review" : "draft", // saving never publishes
    };
    try {
      if (isNew) {
        const r = await axios.post(`${API}/admin/articles`, payload, {
          withCredentials: true,
        });
        toast.success("Article created as a draft — nothing was published.");
        navigate(`/admin/edit/${r.data.id}`, { replace: true });
      } else {
        const r = await axios.put(`${API}/admin/articles/${id}`, payload, {
          withCredentials: true,
        });
        setForm((f) => ({ ...f, status: r.data.status }));
        toast.success("Saved — nothing was published.");
      }
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login", { replace: true });
      else toast.error(e?.response?.data?.detail || "Could not save the article.");
    } finally {
      setSaving(false);
    }
  };

  const act = (action, label) =>
    axios
      .post(`${API}/admin/articles/${id}/${action}`, {}, { withCredentials: true })
      .then((r) => {
        setForm((f) => ({ ...f, status: r.data.status }));
        toast.success(label);
      })
      .catch((e) =>
        toast.error(e?.response?.data?.detail || `Could not ${action} the article.`)
      );

  // ---- body block helpers ----
  const addBlock = (type) =>
    setForm((f) => ({
      ...f,
      body: [
        ...f.body,
        type === "questions" ? { type, items: ["", "", ""] } : { type, text: "" },
      ],
    }));
  const updateBlock = (i, patch) =>
    setForm((f) => ({
      ...f,
      body: f.body.map((b, bi) => (bi === i ? { ...b, ...patch } : b)),
    }));
  const moveBlock = (i, dir) =>
    setForm((f) => {
      const j = i + dir;
      if (j < 0 || j >= f.body.length) return f;
      const body = [...f.body];
      [body[i], body[j]] = [body[j], body[i]];
      return { ...f, body };
    });
  const removeBlock = (i) =>
    setForm((f) => ({ ...f, body: f.body.filter((_, bi) => bi !== i) }));

  const wordCount = useMemo(
    () =>
      form.body
        .map((b) => (b.type === "questions" ? (b.items || []).join(" ") : b.text || ""))
        .join(" ")
        .split(/\s+/)
        .filter(Boolean).length,
    [form.body]
  );

  if (!loaded) {
    return (
      <AdminGuard>
        <div className="flex min-h-[70vh] items-center justify-center">
          <p className="text-sm font-semibold text-charcoal/50">Loading article…</p>
        </div>
      </AdminGuard>
    );
  }

  return (
    <AdminGuard>
      <div className="min-h-screen bg-offwhite" data-testid="admin-editor">
        <header className="border-b border-navy/10 bg-white">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-4">
            <Link to="/" aria-label="English for Leaders — home">
              <Wordmark />
            </Link>
            <Link
              to="/admin"
              data-testid="editor-back-link"
              className="inline-flex items-center gap-2 text-sm font-bold text-navy underline decoration-navy/30 underline-offset-4 hover:decoration-navy"
            >
              <ArrowLeft className="h-4 w-4" />
              Dashboard
            </Link>
          </div>
        </header>

        <main className="mx-auto max-w-4xl px-6 py-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <h1 className="font-display text-2xl tracking-tight text-navy">
                {isNew ? "New article" : "Edit article"}
              </h1>
              <span
                className="rounded-sm border border-navy/20 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-navy"
                data-testid="editor-status-badge"
              >
                {STATUS_LABELS[form.status]}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-testid="editor-preview-toggle"
                onClick={() => setPreview((v) => !v)}
                className="inline-flex items-center gap-2 rounded-sm border border-navy/20 px-4 py-2 text-sm font-bold text-navy transition-colors hover:border-navy"
              >
                {preview ? <Pencil className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {preview ? "Back to editing" : "Preview"}
              </button>
              <button
                type="button"
                data-testid="editor-save-button"
                onClick={save}
                disabled={!canSave || saving}
                className="rounded-sm bg-navy px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-navy-deep disabled:opacity-50"
              >
                {saving ? "Saving…" : isNew ? "Create draft" : "Save"}
              </button>
              {!isNew && !isPublished && form.status !== "archived" && (
                <ConfirmAction
                  label="Publish"
                  confirmLabel="Publish now"
                  testid="editor-publish"
                  className="!px-4 !py-2 !text-sm"
                  onConfirm={() => act("publish", "Article published and added to the sitemap.")}
                />
              )}
              {!isNew && isPublished && (
                <ConfirmAction
                  label="Unpublish"
                  confirmLabel="Unpublish now"
                  testid="editor-unpublish"
                  className="!px-4 !py-2 !text-sm"
                  onConfirm={() => act("unpublish", "Article unpublished (back to draft).")}
                />
              )}
              {!isNew && form.status !== "archived" && (
                <ConfirmAction
                  label="Archive"
                  confirmLabel="Archive now"
                  testid="editor-archive"
                  className="!px-4 !py-2 !text-sm"
                  onConfirm={() => act("archive", "Article archived.")}
                />
              )}
              {!isNew && form.status === "draft" && (
                <button
                  type="button"
                  data-testid="editor-mark-review"
                  onClick={() =>
                    setForm((f) => ({ ...f, status: "review" })) ||
                    toast.success("Marked Ready for Review — save to keep it.")
                  }
                  className="rounded-sm border border-navy/20 px-4 py-2 text-sm font-bold text-navy transition-colors hover:border-navy"
                >
                  Mark ready for review
                </button>
              )}
            </div>
          </div>

          {preview ? (
            <div
              className="mt-8 border border-navy/10 bg-white"
              data-testid="editor-preview"
            >
              <div className="border-b border-navy/10 bg-offwhite px-6 py-12 sm:px-10">
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-crimson">
                  {form.category}
                </p>
                <h2 className="mt-4 font-display text-3xl leading-tight tracking-tight text-navy sm:text-4xl">
                  {form.title || "Untitled"}
                </h2>
                <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-charcoal/50">
                  David Duenzl · Ideas, Questions &amp; Possibilities · {form.read_time}
                </p>
              </div>
              <div className="px-6 py-10 sm:px-10">
                <ArticleBody blocks={form.body} />
              </div>
            </div>
          ) : (
            <div className="mt-8 space-y-6">
              <div className="grid gap-6 border border-navy/10 bg-white p-6 sm:grid-cols-2 sm:p-8">
                <div className="sm:col-span-2">
                  <Field label="Title *" testid="field-title">
                    <input
                      className={inputClass}
                      data-testid="editor-title-input"
                      value={form.title}
                      onChange={onTitle}
                      maxLength={200}
                    />
                  </Field>
                </div>
                <Field
                  label="URL slug *"
                  hint={
                    isPublished
                      ? "Locked while published — unpublish to change the URL."
                      : "Lowercase words joined by hyphens. /insights/" + (form.slug || "…")
                  }
                  testid="field-slug"
                >
                  <input
                    className={`${inputClass} disabled:bg-offwhite disabled:text-charcoal/50`}
                    data-testid="editor-slug-input"
                    value={form.slug}
                    disabled={isPublished}
                    onChange={(e) => {
                      setSlugTouched(true);
                      set("slug")(e);
                    }}
                    maxLength={200}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Category *" testid="field-category">
                    <select
                      className={inputClass}
                      data-testid="editor-category-select"
                      value={form.category}
                      onChange={set("category")}
                    >
                      {ARTICLE_CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Read time" testid="field-readtime">
                    <input
                      className={inputClass}
                      data-testid="editor-readtime-input"
                      value={form.read_time}
                      onChange={set("read_time")}
                      maxLength={30}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Excerpt" testid="field-excerpt">
                    <textarea
                      className={`${inputClass} resize-y`}
                      data-testid="editor-excerpt-input"
                      rows={3}
                      value={form.excerpt}
                      onChange={set("excerpt")}
                      maxLength={500}
                    />
                  </Field>
                </div>
              </div>

              <div className="border border-navy/10 bg-white p-6 sm:p-8">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-xl text-navy">Body</h2>
                  <p className="text-xs font-semibold text-charcoal/50" data-testid="editor-word-count">
                    {wordCount} words
                  </p>
                </div>

                <div className="mt-5 space-y-4">
                  {form.body.map((b, i) => (
                    <div
                      key={i}
                      className="border border-navy/10 bg-offwhite p-4"
                      data-testid={`editor-block-${i}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-navy/60">
                          {BLOCK_LABELS[b.type]}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            aria-label="Move up"
                            data-testid={`block-${i}-up`}
                            onClick={() => moveBlock(i, -1)}
                            className="rounded-sm p-1.5 text-navy/60 transition-colors hover:bg-navy/5 hover:text-navy"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            aria-label="Move down"
                            data-testid={`block-${i}-down`}
                            onClick={() => moveBlock(i, 1)}
                            className="rounded-sm p-1.5 text-navy/60 transition-colors hover:bg-navy/5 hover:text-navy"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            aria-label="Delete block"
                            data-testid={`block-${i}-delete`}
                            onClick={() => removeBlock(i)}
                            className="rounded-sm p-1.5 text-crimson/70 transition-colors hover:bg-crimson/5 hover:text-crimson"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {b.type === "questions" ? (
                        <textarea
                          className={`${inputClass} mt-3 resize-y bg-white`}
                          data-testid={`block-${i}-items`}
                          rows={4}
                          value={(b.items || []).join("\n")}
                          onChange={(e) =>
                            updateBlock(i, {
                              items: e.target.value
                                .split("\n")
                                .map((s) => s.trim())
                                .filter(Boolean),
                            })
                          }
                          placeholder="One reflection question per line"
                        />
                      ) : (
                        <textarea
                          className={`${inputClass} mt-3 resize-y bg-white`}
                          data-testid={`block-${i}-text`}
                          rows={b.type === "p" ? 5 : 2}
                          value={b.text || ""}
                          onChange={(e) => updateBlock(i, { text: e.target.value })}
                          placeholder={
                            b.type === "p"
                              ? "Paragraph text…"
                              : b.type === "h2"
                              ? "Subheading…"
                              : "Try This activity…"
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div
                  className="mt-5 flex flex-wrap gap-2"
                  data-testid="editor-add-block"
                >
                  {[
                    ["p", "Paragraph"],
                    ["h2", "Subheading"],
                    ["questions", "Reflection questions"],
                    ["try", "Try This"],
                  ].map(([type, label]) => (
                    <button
                      key={type}
                      type="button"
                      data-testid={`add-block-${type}`}
                      onClick={() => addBlock(type)}
                      className="inline-flex items-center gap-1.5 rounded-sm border border-navy/20 px-3 py-2 text-xs font-bold text-navy transition-colors hover:border-navy"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-6 border border-navy/10 bg-white p-6 sm:grid-cols-2 sm:p-8">
                <div className="sm:col-span-2">
                  <h2 className="font-display text-xl text-navy">SEO &amp; sharing</h2>
                  <p className="mt-1 text-xs text-charcoal/50">
                    Optional — when left empty, the article title and excerpt are used.
                  </p>
                </div>
                <Field label="SEO title" testid="field-seo-title">
                  <input
                    className={inputClass}
                    data-testid="editor-seo-title-input"
                    value={form.seo_title}
                    onChange={set("seo_title")}
                    maxLength={200}
                  />
                </Field>
                <Field label="SEO description" testid="field-seo-description">
                  <input
                    className={inputClass}
                    data-testid="editor-seo-description-input"
                    value={form.seo_description}
                    onChange={set("seo_description")}
                    maxLength={300}
                  />
                </Field>
                <Field label="Social title (Open Graph)" testid="field-og-title">
                  <input
                    className={inputClass}
                    data-testid="editor-og-title-input"
                    value={form.og_title}
                    onChange={set("og_title")}
                    maxLength={200}
                  />
                </Field>
                <Field label="Social description" testid="field-og-description">
                  <input
                    className={inputClass}
                    data-testid="editor-og-description-input"
                    value={form.og_description}
                    onChange={set("og_description")}
                    maxLength={300}
                  />
                </Field>
              </div>
            </div>
          )}
        </main>
      </div>
    </AdminGuard>
  );
};

export default Editor;
